import React from 'react';
import Icon from '../../../components/AppIcon';

const IntegrationCapabilities = () => {
  const capabilities = [
    {
      id: 1,
      category: "Cloud Infrastructure",
      icon: "Cloud",
      integrations: [
        { name: "Azure Cloud Services", status: "Active", complexity: "Enterprise" },
        { name: "Microsoft 365 Suite", status: "Active", complexity: "Standard" },
        { name: "Zoho Cloud Platform", status: "Active", complexity: "Standard" },
        { name: "Hybrid Cloud Solutions", status: "Active", complexity: "Enterprise" }
      ],
      description: "Seamless cloud migration and management across multiple platforms with enterprise-grade security and scalability."
    },
    {
      id: 2,
      category: "Business Applications",
      icon: "Briefcase",
      integrations: [
        { name: "Dynamics 365 CRM", status: "Active", complexity: "Enterprise" },
        { name: "Zoho CRM & Sales", status: "Active", complexity: "Standard" },
        { name: "Power Platform Apps", status: "Active", complexity: "Standard" },
        { name: "Custom ERP Solutions", status: "Active", complexity: "Enterprise" }
      ],
      description: "Integrated business management systems connecting sales, operations, and customer service for unified workflows."
    },
    {
      id: 3,
      category: "Cybersecurity & Backup",
      icon: "Shield",
      integrations: [
        { name: "Acronis Cyber Protect", status: "Active", complexity: "Enterprise" },
        { name: "Azure Security Center", status: "Active", complexity: "Enterprise" },
        { name: "Backup & Recovery", status: "Active", complexity: "Standard" },
        { name: "Threat Intelligence", status: "Active", complexity: "Enterprise" }
      ],
      description: "Comprehensive security infrastructure with automated backup, disaster recovery, and real-time threat protection."
    },
    {
      id: 4,
      category: "Data & Analytics",
      icon: "BarChart3",
      integrations: [
        { name: "Power BI Analytics", status: "Active", complexity: "Standard" },
        { name: "Zoho Analytics Suite", status: "Active", complexity: "Standard" },
        { name: "Azure Data Services", status: "Active", complexity: "Enterprise" },
        { name: "Custom Dashboards", status: "Active", complexity: "Standard" }
      ],
      description: "Advanced data visualization and business intelligence tools for actionable insights and informed decision-making."
    },
    {
      id: 5,
      category: "Collaboration Tools",
      icon: "Users",
      integrations: [
        { name: "Microsoft Teams", status: "Active", complexity: "Standard" },
        { name: "SharePoint Online", status: "Active", complexity: "Standard" },
        { name: "Zoho Workplace", status: "Active", complexity: "Standard" },
        { name: "Project Management", status: "Active", complexity: "Standard" }
      ],
      description: "Unified communication and collaboration platforms enabling remote work and team productivity across organizations."
    },
    {
      id: 6,
      category: "Automation & AI",
      icon: "Zap",
      integrations: [
        { name: "Power Automate", status: "Beta", complexity: "Standard" },
        { name: "Zoho Flow Automation", status: "Active", complexity: "Standard" },
        { name: "AI Cognitive Services", status: "Beta", complexity: "Enterprise" },
        { name: "RPA Solutions", status: "Active", complexity: "Enterprise" }
      ],
      description: "Intelligent automation and AI-powered workflows reducing manual tasks and accelerating business processes."
    }
  ];

  const getStatusColor = (status) => {
    return status === "Active" ? "text-accent" : "text-warning";
  };

  const getComplexityBadge = (complexity) => {
    return complexity === "Enterprise" ?"bg-primary/10 text-primary" :"bg-secondary/10 text-secondary";
  };

  return (
    <section className="py-12 md:py-16 lg:py-20 px-4 md:px-6 lg:px-8 bg-muted/30">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12 md:mb-16 lg:mb-20">
          <div className="inline-flex items-center gap-2 bg-secondary/10 px-4 py-2 rounded-full mb-4">
            <Icon name="Puzzle" size={18} color="var(--color-secondary)" />
            <span className="text-secondary text-sm font-medium">Integration Matrix</span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Comprehensive Integration Capabilities
          </h2>
          <p className="text-text-secondary text-sm md:text-base lg:text-lg max-w-3xl mx-auto">
            50+ certified integrations across cloud infrastructure, business applications, security, and analytics platforms ensuring seamless digital transformation.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {capabilities?.map((capability) => (
            <div
              key={capability?.id}
              className="bg-card rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden"
            >
              <div className="bg-gradient-to-r from-primary/5 to-secondary/5 p-6 md:p-8 border-b border-border">
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-white shadow-md flex items-center justify-center flex-shrink-0">
                    <Icon name={capability?.icon} size={24} color="var(--color-primary)" />
                  </div>
                  <div>
                    <h3 className="text-lg md:text-xl font-bold text-foreground">{capability?.category}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <Icon name="CheckCircle2" size={14} color="var(--color-accent)" />
                      <span className="text-xs text-text-secondary">{capability?.integrations?.length} Active Integrations</span>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-text-secondary line-clamp-2">
                  {capability?.description}
                </p>
              </div>

              <div className="p-6 md:p-8">
                <div className="space-y-4">
                  {capability?.integrations?.map((integration, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 md:p-4 bg-muted/50 rounded-lg hover:bg-muted transition-colors duration-200"
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="w-2 h-2 rounded-full bg-accent flex-shrink-0"></div>
                        <span className="text-sm font-medium text-foreground truncate">{integration?.name}</span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                        <span className={`text-xs font-medium ${getStatusColor(integration?.status)}`}>
                          {integration?.status}
                        </span>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getComplexityBadge(integration?.complexity)}`}>
                          {integration?.complexity}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 md:mt-16 text-center">
          <div className="inline-flex flex-col md:flex-row items-center gap-4 md:gap-8 bg-card rounded-2xl p-6 md:p-8 shadow-md">
            <div className="flex items-center gap-3">
              <Icon name="Rocket" size={24} color="var(--color-primary)" />
              <div className="text-left">
                <div className="text-2xl md:text-3xl font-bold text-foreground">50+</div>
                <div className="text-xs md:text-sm text-text-secondary">Total Integrations</div>
              </div>
            </div>
            <div className="hidden md:block w-px h-12 bg-border"></div>
            <div className="flex items-center gap-3">
              <Icon name="Zap" size={24} color="var(--color-accent)" />
              <div className="text-left">
                <div className="text-2xl md:text-3xl font-bold text-foreground">98%</div>
                <div className="text-xs md:text-sm text-text-secondary">Success Rate</div>
              </div>
            </div>
            <div className="hidden md:block w-px h-12 bg-border"></div>
            <div className="flex items-center gap-3">
              <Icon name="Clock" size={24} color="var(--color-secondary)" />
              <div className="text-left">
                <div className="text-2xl md:text-3xl font-bold text-foreground">24/7</div>
                <div className="text-xs md:text-sm text-text-secondary">Support Available</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default IntegrationCapabilities;